import React, { useContext, useEffect, useState } from 'react';
import { DiscountBadge, DiscountBadgeVariant, UserContext } from '@secretescapes/cauldron';
import {
  Gallery as FalconGallery,
  type GalleryElement,
  Lightbox,
  ModalGallery,
  useTheme,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useTranslations } from '@utils/useTranslations';
import { InteractionButtons } from '@features/Sale/InteractionButtons';
import { useFavourite } from '@features/hooks/useFavourite';
import { GalleryOverlay, StyledDiscountBadge } from './Gallery.styles';
import { Modal } from './Gallery.utils';
import { GalleryAction } from './GalleryAction';
import { useGalleryNavigation } from './useGalleryNavigation';

const THUMBS_COUNT = 4;

interface Props {
  images: GalleryElement[];
  saleId: string;
  saleTitle: string;
  isFavourite: boolean;
  isMobile: boolean;
  showUrgentDiscount: boolean;
  discount: string;
  discountTooltip?: string;
  refetchFavourites: () => void;
}

export const Gallery = ({
  images,
  saleId,
  saleTitle,
  isFavourite,
  isMobile,
  showUrgentDiscount,
  discount,
  discountTooltip,
  refetchFavourites,
}: Props) => {
  const theme = useTheme();
  const { userId } = useContext(UserContext);
  const { translations } = useTranslations();
  const [focusedLightboxImage, setFocusedLightboxImage] = useState(0);
  const [focusedImageIndex, setFocusedImageIndex] = useState<number | undefined>();
  const { closeModal, openModal, showLightbox, showGalleryModal } = useGalleryNavigation();

  const handleOpenModal = (imageIndex?: number) => {
    if (userId) {
      refetchFavourites();
    }

    openModal(Modal.gallery);
    setFocusedImageIndex(imageIndex);
  };

  const handleImageClick = (imageIndex: number) => {
    openModal(Modal.lightbox);
    setFocusedLightboxImage(imageIndex);
  };

  const handleCloseModal = () => {
    if (userId) {
      refetchFavourites();
    }

    closeModal(Modal.gallery);
    setFocusedImageIndex(undefined);
  };

  const { handleFavouriteChange, buttonText } = useFavourite({ saleId, translations });
  const [localFavouriteState, setLocalFavouriteState] = useState(isFavourite);

  useEffect(() => {
    setLocalFavouriteState(isFavourite);
  }, [isFavourite]);

  return (
    <>
      <FalconGallery
        elements={images}
        thumbsCount={THUMBS_COUNT}
        onImageClick={handleOpenModal}
        isMobileIntrinsicSize={isMobile}
        showImageTitleOnCarousel={false}
      >
        <>
          <GalleryOverlay isMobile={isMobile}>
            {isMobile && (
              <InteractionButtons
                saleId={saleId}
                saleTitle={saleTitle}
                isFavourite={localFavouriteState}
                isMobile={isMobile}
              />
            )}
            {showUrgentDiscount ? (
              <StyledDiscountBadge>
                <DiscountBadge
                  discountPrice={discount}
                  variant={DiscountBadgeVariant.urgent}
                  discountTooltip={discountTooltip}
                  showTooltip={!!discountTooltip}
                  data-testid="discount-badge"
                  theme={theme}
                />
              </StyledDiscountBadge>
            ) : null}
          </GalleryOverlay>
          <GalleryAction onClick={handleOpenModal} totalImages={images.length} />
        </>
      </FalconGallery>
      <ModalGallery
        images={images}
        isOpen={showGalleryModal}
        onClose={handleCloseModal}
        focusedImageIndex={focusedImageIndex}
        onImageClick={isMobile ? undefined : handleImageClick}
        showImageTitles
        backButtonText={i18n('mobile.backButton', [], translations)}
        heartIconProps={{
          checked: localFavouriteState,
          onChange: checked => {
            const previousFavouriteState = localFavouriteState;
            setLocalFavouriteState(checked);
            handleFavouriteChange(checked).catch(() => {
              setLocalFavouriteState(previousFavouriteState);
            });
          },
          buttonText: isMobile ? undefined : buttonText,
        }}
        shareIconProps={{
          title: saleTitle,
          text: saleTitle,
          shareLabel: i18n(
            'sale.confirmPayement.shareYourTripDetails.share.label',
            [],
            translations
          ),
        }}
      />
      {showLightbox && !isMobile && (
        <Lightbox
          dataAttributes={{ testid: 'gallery-lightbox' }}
          closeButtonText={i18n('common.close.label', [], translations)}
          /* Lightbox will downscale to fit viewport */
          images={images.map(img => ({ ...img, width: 4000, height: 2667 }))}
          infiniteCarousel={true}
          initialSlide={focusedLightboxImage}
          onClose={() => closeModal(Modal.lightbox)}
          showNavigation={true}
          animation={true}
        />
      )}
    </>
  );
};
